import { Link } from 'react-router-dom'
import { toCategoryTile, toPostCard } from '@/api/adapters'
import { useFeaturedTagsQuery, useSearchPostsQuery } from '@/api/queries'
import { CategoryTile } from '@/components/CategoryTile'
import { ChevronRightIcon, DownloadIcon, RefreshIcon } from '@/components/icons'
import { ModCard, ModCardSkeleton } from '@/components/ModCard'
import { EmptyState, ErrorState, SectionHeader } from '@/components/ui'
import { DEFAULT_DESCRIPTION, DEFAULT_TITLE, usePageMeta } from '@/hooks'
import { cn } from '@/utils/cn'

/**
 * 首页（设计稿 D1 桌面 / M1 移动）。
 *
 * 三个区块，自上而下：
 *   1. **下载最多** —— 4 条，带排行角标。移动端横向滚动 rail。
 *   2. **分类磁贴** —— 运营配置的展示位（`GET /featured-tags`，可带封面），
 *      深色底 + 主色斜切角，移动端横向滚动。
 *   3. **最新更新** —— 多行网格，移动端 2 列。
 *
 * 两个区块各自独立取数、独立处理三态：一个区块失败不该让整页变成错误页 ——
 * 「下载最多」挂了，用户照样能浏览「最新更新」。
 *
 * ## 三排一律 4 列（`GRID_COLUMNS`）
 *
 * 设计稿画的是 5 列，按项目负责人 2026-08-11 的要求改成 4 列。三排共用同一个
 * 列数不只是为了好看：**上面两排的条数正好等于列数**，列数与条数不一致就会掉出
 * 一张孤零零的卡（5 条 4 列 = 第二行只有一张）。所以 `TOP_LIMIT` / `TILE_LIMIT`
 * 与列数是同一个数字，改列数必须同时改它们。
 *
 * 断点选 `md`（768px）而不是 `lg`：卡片本身不宽，768px 下 4 列每张约 175px，
 * 与移动端 2 列时的宽度相当，不会挤成三行标题。`sm`（640px）仍然是 2 列。
 */

/** 桌面端每行几个。见文件头：上面两排的条数必须等于它。 */
const COLUMNS = 4

/** 三排共用的列数类名。写成常量是为了让「三排必须一致」这件事在代码里也成立。 */
const GRID_COLUMNS = 'sm:grid-cols-2 md:grid-cols-4'

/** 排行 4 条、分类磁贴 4 块（都等于列数），最新更新首屏 20 条（正好 5 行）。 */
const TOP_LIMIT = COLUMNS
const LATEST_LIMIT = 20
const TILE_LIMIT = COLUMNS

export function HomePage() {
  // 首页的值本就是 index.html 里的兜底默认值，这里显式设一遍是为了让
  // canonical / og:url 落到 `/` 而不是挂上一个页面 unmount 时留下的路径。
  usePageMeta({ title: DEFAULT_TITLE, description: DEFAULT_DESCRIPTION, path: '/' })

  const topQuery = useSearchPostsQuery({ sort: 'downloads', limit: TOP_LIMIT })
  const latestQuery = useSearchPostsQuery({ sort: 'latest', limit: LATEST_LIMIT })
  const featuredQuery = useFeaturedTagsQuery({ limit: TILE_LIMIT })

  /**
   * 分类磁贴的取值。
   *
   * 设计稿画了 5 块。**内容来自运营配置的展示位** —— 后端 2026-08-11 起有了
   * `GET /featured-tags`（独立的 `featured_tags` 表），一格一个标签 + 一张封面，
   * 次序就是返回的数组顺序。管理入口在「作者与标签」页的「首页展示位」区块。
   *
   * 展示位一格都没配（或还没取到数据）时**整段不展示**，不再退回按标签取前几个
   * 填充：这一排本来就是运营位，没配就该是空的，不该拿标签凑数。
   */
  const tiles = (featuredQuery.data ?? []).map(toCategoryTile)

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* ------------------------------------------------------ 下载最多 */}
      <section aria-labelledby="section-top-downloads" className="space-y-3">
        <SectionHeader
          icon={<DownloadIcon className="size-[1.125rem]" />}
          title="下载最多"
          action={<SeeAllLink to="/ranking" />}
        />

        {topQuery.isError && topQuery.data === undefined ? (
          <ErrorState error={topQuery.error} onRetry={topQuery.refetch} />
        ) : (
          /*
            移动端用横向 rail（设计稿 M1）：窄屏里几张卡竖排会把「最新更新」
            推到首屏之外。sm 起换成网格。
          */
          <ul
            className={cn(
              'snap-rail -mx-3 flex gap-3 overflow-x-auto px-3 pb-1 sm:mx-0 sm:grid sm:overflow-visible sm:px-0',
              GRID_COLUMNS,
            )}
          >
            {topQuery.data
              ? topQuery.data.items.map((item, index) => (
                  <li key={item.slug} className="w-[46%] shrink-0 snap-start sm:w-auto sm:shrink">
                    <ModCard post={toPostCard(item)} rank={index + 1} />
                  </li>
                ))
              : Array.from({ length: TOP_LIMIT }, (_, index) => (
                  <li key={index} className="w-[46%] shrink-0 sm:w-auto sm:shrink">
                    <ModCardSkeleton />
                  </li>
                ))}
          </ul>
        )}
        <h2 id="section-top-downloads" className="sr-only">
          下载最多的资源
        </h2>
      </section>

      {/* ------------------------------------------------------ 分类磁贴 */}
      {/* 展示位没配置、还没加载完或取数失败时整段不渲染：磁贴是运营入口而不是
          内容，没配就该是空的，为它做骨架屏或错误态只会让首屏多闪一次。 */}
      {tiles.length > 0 && (
        <section aria-labelledby="section-categories">
          <h2 id="section-categories" className="sr-only">
            浏览分类
          </h2>
          <ul
            className={cn(
              'snap-rail -mx-3 flex gap-3 overflow-x-auto px-3 pb-1 sm:mx-0 sm:grid sm:overflow-visible sm:px-0',
              GRID_COLUMNS,
            )}
          >
            {tiles.map((tile) => (
              <li key={tile.key} className="w-[40%] shrink-0 snap-start sm:w-auto sm:shrink">
                <CategoryTile tile={tile} />
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* ------------------------------------------------------ 最新更新 */}
      <section aria-labelledby="section-latest" className="space-y-3">
        <SectionHeader
          icon={<RefreshIcon className="size-[1.125rem]" />}
          title="最新更新"
          action={<SeeAllLink to="/search?sort=latest" />}
        />
        <h2 id="section-latest" className="sr-only">
          最新更新的资源
        </h2>

        {latestQuery.isError && latestQuery.data === undefined ? (
          <ErrorState error={latestQuery.error} onRetry={latestQuery.refetch} />
        ) : latestQuery.data && latestQuery.data.items.length === 0 ? (
          <EmptyState
            title="还没有已发布的内容"
            description="站点刚刚上线，或者内容尚未通过发布流程。稍后再来看看。"
          />
        ) : (
          <ul className={cn('grid grid-cols-2 gap-3', GRID_COLUMNS)}>
            {latestQuery.data
              ? latestQuery.data.items.map((item) => (
                  <li key={item.slug}>
                    <ModCard post={toPostCard(item)} />
                  </li>
                ))
              : // 8 = 整两行，骨架屏不留半行（卡片高度固定，行数因此是准的）。
                Array.from({ length: COLUMNS * 2 }, (_, index) => (
                  <li key={index}>
                    <ModCardSkeleton />
                  </li>
                ))}
          </ul>
        )}
      </section>
    </div>
  )
}

/** 「查看全部 ›」（设计稿 D1 每个区块右上角）。 */
function SeeAllLink({ to }: { to: string }) {
  return (
    <Link
      to={to}
      className="inline-flex items-center gap-0.5 rounded-lg px-1.5 py-1 text-[0.8125rem] font-medium text-primary transition-colors hover:text-primary-hover"
    >
      查看全部
      <ChevronRightIcon className="size-4" />
    </Link>
  )
}
